/**
 * Shared constants for toolbar components
 *
 * These values are used by both standard and Procore toolbar implementations.
 * Colors use Procore design tokens where available, with custom values for
 * divergent design requirements.
 */

import { colors, spacing } from "@procore/core-react";

// =============================================================================
// Colors (shared across all toolbar variants)
// =============================================================================
export const TOOLBAR_COLORS = {
    ACTIVE: colors.blue50,
    DEFAULT: colors.gray15,
    HOVER: colors.gray90,
    /** Custom - no exact Procore match */
    HOVER_DARK: "#d0d4d8",
    ACTIVE_BG: colors.blue96,
    /** Custom - no exact Procore match */
    ACTIVE_HOVER: "#BCD1F5",
    DISABLED: colors.gray50,
    /** Custom - action-specific */
    SAVE_GREEN: "#4CAF50",
    SAVE_BG: "#E8F5E9",
    EXIT_RED: "#F44336",
    EXIT_BG: "#FFEBEE",
    /** Custom - no exact Procore match */
    EXPANDER_ACTIVE: "#1952B3",
    EXPANDER_DEFAULT: colors.gray45,
    WHITE: colors.white,
    BLACK: colors.black,
    LIGHT_GREY: colors.gray96,
    BORDER: colors.gray90,
    BADGE_BG: colors.blue98,
    /** Custom - no exact Procore match */
    BADGE_TEXT: "#1976d2",
    TOOLTIP_BG: colors.gray20,
    PANEL_BG: colors.gray96,
    /** Custom - no exact Procore match */
    FLYOUT_GREY: "#C3C3C3",
} as const;

// =============================================================================
// Button sizing (standard toolbar buttons)
// =============================================================================
export const TOOLBAR_BUTTON = {
    SIZE: 36,
    RADIUS: spacing.xs,
    ICON_SIZE: 24,
    PADDING: 6,
} as const;

// =============================================================================
// Toolbar container styling
// =============================================================================
export const TOOLBAR_CONTAINER = {
    BG: colors.white,
    BORDER: `1px solid ${colors.gray90}`,
    RADIUS: 8,
    SHADOW: "0px 0px 4px 0px #00000040",
    PADDING: spacing.xs,
    GAP: spacing.xs,
    WIDTH: 44,
} as const;

// =============================================================================
// Procore-specific: Bottom toolbar entry sizing (from Figma)
// =============================================================================
export const PROCORE_BOTTOM_ENTRY = {
    WIDTH: 56,
    ICON_SIZE: 48,
    TEXT_WIDTH: 56,
    TEXT_HEIGHT: 16,
    TOOLBAR_HEIGHT: 72,
    GAP: 2,
} as const;

// =============================================================================
// Procore-specific: Top bar sizing (from Figma)
// =============================================================================
export const PROCORE_TOP_BAR = {
    HEIGHT: 64,
    HEIGHT_PORTRAIT: 80, // Taller in phone portrait due to vertical dropdown layout
    CLOSE_SIZE: 40,
    NAME_WIDTH: 213,
    REVISION_WIDTH: 236,
    ACTION_SIZE: 40,
    /** 20% opacity white overlay */
    BG: "rgba(255, 255, 255, 0.2)",
} as const;

// =============================================================================
// Procore-specific: Undo/Redo group sizing (from Figma)
// =============================================================================
export const PROCORE_UNDO_GROUP = {
    WIDTH: 164,
    HEIGHT: 66,
    GAP: spacing.xs,
    BUTTON_WIDTH: 52,
    BUTTON_HEIGHT: 66,
} as const;

// =============================================================================
// Layout constants
// =============================================================================
export const TOOLBAR_LAYOUT = {
    MARGIN_MOBILE: 8,
    MARGIN_TABLET: 16,
    GROUP_GAP: spacing.sm,
} as const;

// =============================================================================
// Joysticks layout constants (4 is knob outline size)
// =============================================================================
export const JOYSTICKS_LAYOUT = {
    MARGIN_TABLET: 32 + 4,
    MARGIN_MOBILE: 8 + 4,
    MARGIN_BOTTOM_MOBILE: 8 + 4,
    MARGIN_BOTTOM_TABLET_LANDSCAPE: 24,
    MARGIN_BOTTOM_TABLET_PORTRAIT: 144,
    MARGIN_BOTTOM_TABLET_PORTRAIT_MIN_WIDTH: 700,
} as const;

// =============================================================================
// Z-indices
// =============================================================================
export const TOOLBAR_Z_INDEX = {
    LARGE_MAP_WEB: 1110,
    TOP_BAR: 1060,
    LARGE_MAP_MOBILE: 1055,
    TOOLBAR: 1050,
    SIDE_PANEL: 1040,
} as const;

// =============================================================================
// Animation
// =============================================================================
export const TOOLBAR_ANIMATION = {
    DURATION: 200,
    EASING: "ease-out",
    DURATION_OPEN: 300,
    EASING_OPEN: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

// =============================================================================
// Tool IDs (for Procore bottom toolbar)
// =============================================================================
export const PROCORE_TOOL_IDS = {
    MARKUP: "markup",
    CREATE: "create",
    MEASURE: "measure",
    SECTION: "section",
} as const;

// =============================================================================
// Minimap layout constants
// =============================================================================
export const MINIMAP_LAYOUT = {
    MINIMAP_MIN_HEIGHT: 360,
    Z_INDEX: 1050,
} as const;

export const QUICK_ACCESS_TOOLBAR = {
    FLYOUT_Z_INDEX: 1100,
} as const;
